/**
 * POST /storage/upload - Upload a file (Hono)
 *
 * Accepts multipart/form-data with:
 * - file: The file to upload (required)
 * - folder: Optional folder path
 * - scope: Storage config scope
 * - dedupe: Enable deduplication by hash
 * - visibility: File visibility level (public, internal, or private)
 */

import { tmpdir } from 'node:os'
import { join } from 'node:path'
import { writeFile, unlink } from 'node:fs/promises'
import type { ActionDefinition } from '@gzl10/nexus-sdk'
import { getStorageService } from '../storage.service.js'
import type { UploadedFile } from '../storage.types.js'
import { getVideoProcessingService, isVideoMimetype } from '../video-processing.service.js'
import { refineMime } from '../mime.utils.js'
import { extractUploadOptions } from './helpers.js'

export const uploadAction: ActionDefinition = {
  key: 'upload',
  scope: 'module',
  group: { en: 'File Management', es: 'Gestión de archivos' },
  label: { en: 'Upload File', es: 'Cargar archivo' },
  icon: 'mdi:upload',
  successStatus: 201,
  output: {
    filename: { label: { en: 'Filename', es: 'Nombre' }, input: 'text' },
    mimetype: { label: { en: 'Type', es: 'Tipo' }, input: 'text' },
    size: { label: { en: 'Size', es: 'Tamaño' }, input: 'number', displayProps: { format: 'bytes' } },
    visibility: { label: { en: 'Visibility', es: 'Visibilidad' }, input: 'text' },
    url: { label: { en: 'URL', es: 'URL' }, input: 'url' }
  },
  postAction: [
    { type: 'notification', message: 'File uploaded: $result.filename' },
    { type: 'refreshAll' }
  ],

  input: {
    file: {
      label: { en: 'File', es: 'Archivo' },
      input: 'file',
      required: true
    },
    folder: {
      label: { en: 'Folder', es: 'Carpeta' },
      input: 'text',
      hint: { en: 'Optional folder path', es: 'Ruta de carpeta opcional' }
    },
    scope: {
      label: { en: 'Storage Scope', es: 'Ámbito de almacenamiento' },
      input: 'select',
      hint: { en: 'Storage config scope (default: uses default config)', es: 'Ámbito de configuración de almacenamiento (default: usa la configuración por defecto)' },
      options: { endpoint: '/storage/config', valueField: 'scope', labelField: 'scope' }
    },
    visibility: {
      input: 'select',
      label: { en: 'Visibility', es: 'Visibilidad' },
      defaultValue: 'internal',
      options: {
        static: [
          { value: 'public', label: { en: 'Public', es: 'Público' } },
          { value: 'internal', label: { en: 'Internal', es: 'Interno' } },
          { value: 'private', label: { en: 'Private', es: 'Privado' } }
        ]
      }
    }
  },

  handler: async (ctx, _input, c) => {
    if (!c) throw new ctx.core.errors.AppError('Hono Context required for upload', 500)

    // Parse multipart body — Web standard
    const body = await c.req.parseBody({ all: true })
    const fileRaw = body['file']
    const fileEntry = Array.isArray(fileRaw) ? fileRaw[0] : fileRaw

    if (!fileEntry || !(fileEntry instanceof File)) {
      throw new ctx.core.errors.ValidationError('No file provided')
    }

    // Reject filenames with null bytes (security)
    if (fileEntry.name.includes('\x00')) {
      throw new ctx.core.errors.ValidationError('Invalid filename: contains null byte')
    }

    const buffer = Buffer.from(await fileEntry.arrayBuffer())
    const file: UploadedFile = {
      fieldname: 'file',
      originalname: fileEntry.name,
      mimetype: refineMime(fileEntry.type, fileEntry.name),
      buffer,
      size: fileEntry.size
    }

    const user = c.get('user') as { id: string } | undefined
    const options = { ...extractUploadOptions(body as Record<string, unknown>), userId: user?.id }
    const storageService = getStorageService()

    const result = await storageService.upload(file, options)

    // Extract video metadata in background (fire-and-forget)
    if (isVideoMimetype(result.mimetype)) {
      const uploadBuffer = file.buffer
      const uploadId = result.id
      void (async () => {
        const tmpPath = join(tmpdir(), `nexus-meta-${uploadId}`)
        try {
          const videoSvc = getVideoProcessingService()
          if (await videoSvc.isAvailable()) {
            await writeFile(tmpPath, uploadBuffer)
            const meta = await videoSvc.getMetadata(tmpPath)
            await ctx.db.knex('storage_files')
              .where({ id: uploadId })
              .update({ metadata: JSON.stringify(meta) })
          }
        } catch { /* non-critical: metadata extraction failure doesn't affect upload */ }
        finally { await unlink(tmpPath).catch(() => {}) }
      })()
    }

    return result as unknown as Record<string, unknown>
  },

  casl: {
    subject: 'StorageFile',
    permissions: {
      EDITOR: { actions: ['execute'] },
      USER: { actions: ['execute'] }
    }
  }
}
